import { PermissionError } from "./permissionErrors";
import {
  guestDemoGrant,
  isGuestActor,
  isGuestTourCapability,
} from "./guestMode";
import {
  isMissionControlCapability,
  isMissionControlEnabled,
} from "../config/releaseFeatures";

/** UI access modes for navigation and controls. */
export const VISIBILITY = Object.freeze({
  HIDDEN: "hidden",
  SELF: "self",
  READ: "read",
  EDIT: "edit",
});

/** Data authorization depth returned by scope-aware capabilities. */
export const DATA_SCOPE = Object.freeze({
  SELF: "self",
  TEAM: "team",
  ORGANIZATION: "organization",
  ASSIGNED: "assigned",
});

export const ROLE = Object.freeze({
  ADMIN: "admin",
  MANAGER: "manager",
  EMPLOYEE: "employee",
  GUEST: "guest",
});

/**
 * Canonical capability keys. Use these constants at call sites when practical.
 * String literals matching these values are also valid for can("invoice.review").
 */
export const CAPABILITY = Object.freeze({
  SESSION_CLOCK: "session.clock",
  SESSION_FINALIZE: "session.finalize",
  SESSION_MATERIALS_COMMIT: "session.materials.commit",
  TIMELOG_VIEW: "timelog.view",
  TIMELOG_EDIT: "timelog.edit",
  TIMELOG_DELETE: "timelog.delete",
  PROJECT_VIEW: "project.view",
  PROJECT_DETAIL_OPERATIONAL: "project.detail.operational",
  PROJECT_DETAIL_FINANCIAL: "project.detail.financial",
  PROJECT_CREATE: "project.create",
  PROJECT_EDIT: "project.edit",
  PROJECT_ARCHIVE: "project.archive",
  CLIENT_CREATE: "client.create",
  CLIENT_EDIT: "client.edit",
  CLIENT_ARCHIVE: "client.archive",
  CLIENT_DELETE: "client.delete",
  /** Read-only Atomic Destruction blast-radius planner (owner/admin). */
  CLIENT_BLAST_RADIUS: "client.blast_radius",
  MATERIAL_CATALOG_SEARCH: "material.catalog.search",
  MATERIAL_CATALOG_VIEW_PRICING: "material.catalog.view.pricing",
  MATERIAL_CATALOG_EDIT: "material.catalog.edit",
  MATERIAL_USAGE_VIEW: "material.usage.view",
  MATERIAL_USAGE_MANUAL: "material.usage.manual",
  MATERIAL_USAGE_VOID: "material.usage.void",
  TASK_CATALOG_VIEW: "task.catalog.view",
  TASK_CATALOG_EDIT: "task.catalog.edit",
  PROJECT_TASK_VIEW: "project.task.view",
  PROJECT_TASK_MANAGE: "project.task.manage",
  PROJECT_TASK_COMPLETE: "project.task.complete",
  PROJECT_CLIENT_VIEW_MANAGE: "project.client_view.manage",
  ESTIMATE_VIEW: "estimate.view",
  ESTIMATE_EDIT: "estimate.edit",
  ESTIMATE_APPROVE: "estimate.approve",
  ESTIMATE_CONVERT: "estimate.convert",
  INVOICE_VIEW: "invoice.view",
  INVOICE_REVIEW: "invoice.review",
  INVOICE_SEND: "invoice.send",
  INVOICE_MARK_PAID: "invoice.mark_paid",
  FINANCIAL_QUOTED_VIEW: "financial.quoted.view",
  FINANCIAL_ACTUAL_VIEW: "financial.actual.view",
  FINANCIAL_BILLED_VIEW: "financial.billed.view",
  FINANCIAL_MARGIN_VIEW: "financial.margin.view",
  PAYROLL_QUEUE: "payroll.queue",
  PAYROLL_REPORT: "payroll.report",
  PAYROLL_EXPORT: "payroll.export",
  PAYROLL_MARK_PAID: "payroll.mark_paid",
  PAYROLL_RATES_EDIT: "payroll.rates.edit",
  TRADES_EDIT: "trades.edit",
  USERS_VIEW: "users.view",
  USERS_EDIT: "users.edit",
  USERS_EDIT_REPORTS: "users.edit.reports",
  DASHBOARD_ACTIVE_CREW: "dashboard.active_crew",
  ACCOUNTS_PAYABLE_VIEW: "accounts_payable.view",
  ACCOUNTS_RECEIVABLE_VIEW: "accounts_receivable.view",
  MISSION_CONTROL_OPERATIONAL: "mission_control.operational.view",
  MISSION_CONTROL_DEVELOPMENT: "mission_control.development.view",
  REPORT_BUILDER_VIEW: "report.builder.view",
  PROJECT_ANALYTICS_VIEW: "project.analytics.view",
  SCREEN_MODE_SELECT: "screen.mode_select",
  SCREEN_KPI_DASHBOARD: "screen.kpi_dashboard",
  SCREEN_TIME_TRACKING: "screen.time_tracking",
  SCREEN_COCKPIT: "screen.cockpit",
  SCREEN_MANUAL_TIME_ENTRY: "screen.manual_time_entry",
  SCREEN_CREATE_CLIENT: "screen.create_client",
  SCREEN_CREATE_PROJECT: "screen.create_project",
  SCREEN_PROJECT_DETAIL: "screen.project_detail",
  SCREEN_TASKS: "screen.tasks",
  SCREEN_PLANNING: "screen.planning",
  SCREEN_INTELLIGENCE: "screen.intelligence",
  SCREEN_PAYROLL_HOME: "screen.payroll_home",
  SCREEN_USER_MANAGEMENT: "screen.user_management",
  SCREEN_USER_PREFERENCES: "screen.user_preferences",
  SCREEN_ADMINISTRATION: "screen.administration",
  SCREEN_SETTINGS: "screen.settings",
  ADMIN_SETTINGS_VIEW: "admin.settings.view",
  ADMIN_ORGANIZATION_MANAGE: "admin.organization.manage",
  ADMIN_BILLING_VIEW: "admin.billing.view",
  ADMIN_USERS_MANAGE: "admin.users.manage",
  ADMIN_ROLES_MANAGE: "admin.roles.manage",
  ADMIN_TRADES_MANAGE: "admin.trades.manage",
  ADMIN_CLASSIFICATIONS_MANAGE: "admin.classifications.manage",
  ADMIN_WORK_TYPES_MANAGE: "admin.work_types.manage",
  ADMIN_PAYROLL_TYPES_MANAGE: "admin.payroll_types.manage",
  ADMIN_PAYROLL_SETTINGS: "admin.payroll.settings",
  ADMIN_PROJECTS_SETTINGS: "admin.projects.settings",
  ADMIN_MATERIALS_SETTINGS: "admin.materials.settings",
  ADMIN_ESTIMATES_SETTINGS: "admin.estimates.settings",
  ADMIN_INVOICES_SETTINGS: "admin.invoices.settings",
  ADMIN_REPORTS_SETTINGS: "admin.reports.settings",
  ADMIN_NOTIFICATIONS_SETTINGS: "admin.notifications.settings",
  ADMIN_OFFLINE_SETTINGS: "admin.offline.settings",
  ADMIN_HELP_LEARNING_SETTINGS: "admin.help_learning.settings",
  ADMIN_BRANDING_MANAGE: "admin.branding.manage",
  ADMIN_INTEGRATIONS_MANAGE: "admin.integrations.manage",
  ADMIN_FEATURES_MANAGE: "admin.features.manage",
  ADMIN_ADVANCED_MANAGE: "admin.advanced.manage",
  SCREEN_ESTIMATE_HUB: "screen.estimate_hub",
  SCREEN_INVOICE_HUB: "screen.invoice_hub",
  SCREEN_MATERIALS: "screen.materials",
  SCREEN_ACCOUNTS_PAYABLE: "screen.accounts_payable",
  SCREEN_ACCOUNTS_RECEIVABLE: "screen.accounts_receivable",
  SCREEN_MISSION_CONTROL: "screen.mission_control",
  FINANCE_MODULE: "finance.module",
  FINANCE_MANAGE: "finance.manage",
  FINANCE_RECONCILE: "finance.reconcile",
  SCREEN_FINANCE: "screen.finance",
});

const MANAGER_PLUS = new Set([ROLE.ADMIN, ROLE.MANAGER]);

const DEFAULT_ORG_POLICIES = Object.freeze({
  payrollSelfService: true,
  financeModuleEnabled: false,
  financeManagerAccess: false,
});

function hidden() {
  return {
    allowed: false,
    scope: null,
    visibility: VISIBILITY.HIDDEN,
  };
}

function grant(scope, visibility) {
  return {
    allowed: true,
    scope,
    visibility,
  };
}

/**
 * Resolve organization policy flags.
 * Future Org V1: pass organizations.settings from CurrentOrganizationProvider.
 */
export function resolveOrgPolicies(orgSettings = {}) {
  const envFlag = process.env.REACT_APP_PAYROLL_SELF_SERVICE;
  const envEnabled = envFlag === undefined || envFlag === "true";

  const financeEnabled =
    orgSettings.modules?.finance?.enabled === true ||
    orgSettings.finance_module_enabled === true;

  const financeManagerAccess =
    orgSettings.modules?.finance?.manager_access === true ||
    orgSettings.finance_manager_access === true;

  return {
    ...DEFAULT_ORG_POLICIES,
    payrollSelfService:
      orgSettings.payroll_self_service !== false && envEnabled,
    financeModuleEnabled: financeEnabled,
    financeManagerAccess,
  };
}

/**
 * Normalize the actor record used across the app (users row + guest shim).
 */
export function normalizeActor(user) {
  if (!user) {
    return null;
  }

  return {
    ...user,
    role: getActorRole(user),
    payroll_access: user.payroll_access === true,
    finance_access: user.finance_access === true,
    isGuest: user.isGuest === true,
  };
}

/**
 * The only supported place to read user.role for authorization logic.
 */
export function getActorRole(user) {
  if (!user || user.isGuest) {
    return ROLE.GUEST;
  }

  const role = user.role;

  if (role === ROLE.ADMIN || role === ROLE.MANAGER || role === ROLE.EMPLOYEE) {
    return role;
  }

  return ROLE.EMPLOYEE;
}

export function isAdmin(user) {
  return getActorRole(user) === ROLE.ADMIN;
}

export function isManager(user) {
  return getActorRole(user) === ROLE.MANAGER;
}

export function isEmployee(user) {
  return getActorRole(user) === ROLE.EMPLOYEE;
}

export function isGuest(user) {
  return getActorRole(user) === ROLE.GUEST;
}

export function isManagerOrAdmin(user) {
  return MANAGER_PLUS.has(getActorRole(user));
}

function employeePayrollAllowed(policies) {
  return policies.payrollSelfService === true;
}

function managerPayrollOrgAllowed(user) {
  return isAdmin(user) || (isManager(user) && user.payroll_access === true);
}

function evaluatePayrollCapability(user, policies, scope, visibility) {
  const role = getActorRole(user);

  if (role === ROLE.GUEST) {
    return hidden();
  }

  if (role === ROLE.ADMIN) {
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  }

  if (role === ROLE.MANAGER) {
    return grant(DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  }

  if (employeePayrollAllowed(policies)) {
    return grant(scope, visibility);
  }

  return hidden();
}

const CAPABILITY_EVALUATORS = {
  [CAPABILITY.SESSION_CLOCK]: (user) => {
    if (isGuest(user)) return hidden();
    return grant(DATA_SCOPE.SELF, VISIBILITY.EDIT);
  },
  [CAPABILITY.SESSION_FINALIZE]: (user) => {
    if (isGuest(user)) return hidden();
    return grant(DATA_SCOPE.SELF, VISIBILITY.EDIT);
  },
  [CAPABILITY.SESSION_MATERIALS_COMMIT]: (user) => {
    if (isGuest(user)) return hidden();
    return grant(DATA_SCOPE.SELF, VISIBILITY.EDIT);
  },
  [CAPABILITY.TIMELOG_VIEW]: (user) => {
    if (isGuest(user)) return hidden();
    if (isAdmin(user)) return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
    if (isManager(user)) return grant(DATA_SCOPE.TEAM, VISIBILITY.EDIT);
    return grant(DATA_SCOPE.SELF, VISIBILITY.SELF);
  },
  [CAPABILITY.TIMELOG_EDIT]: (user) => {
    if (isGuest(user)) return hidden();
    if (isAdmin(user)) return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
    if (isManager(user)) return grant(DATA_SCOPE.TEAM, VISIBILITY.EDIT);
    return grant(DATA_SCOPE.SELF, VISIBILITY.EDIT);
  },
  [CAPABILITY.TIMELOG_DELETE]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.PROJECT_VIEW]: (user) => {
    if (isGuest(user)) return hidden();
    if (isAdmin(user) || isManager(user) || isEmployee(user)) {
      return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
    }
    return hidden();
  },
  [CAPABILITY.PROJECT_DETAIL_OPERATIONAL]: (user, context) => {
    if (isGuest(user)) return hidden();
    if (isManagerOrAdmin(user)) {
      return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
    }
    if (isEmployee(user)) {
      if (context.projectAssigned === false) {
        return hidden();
      }
      return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.SELF);
    }
    return hidden();
  },
  [CAPABILITY.PROJECT_DETAIL_FINANCIAL]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    if (isAdmin(user)) return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
    return grant(DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  [CAPABILITY.PROJECT_CREATE]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    return grant(DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  [CAPABILITY.PROJECT_EDIT]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    return grant(isAdmin(user) ? DATA_SCOPE.ORGANIZATION : DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  [CAPABILITY.PROJECT_ARCHIVE]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    return grant(isAdmin(user) ? DATA_SCOPE.ORGANIZATION : DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  [CAPABILITY.CLIENT_CREATE]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    return grant(DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  [CAPABILITY.CLIENT_EDIT]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    return grant(isAdmin(user) ? DATA_SCOPE.ORGANIZATION : DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  [CAPABILITY.CLIENT_ARCHIVE]: (user) => {
    // Organization administrators/owners only (legacy admin maps owner/admin membership).
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.CLIENT_DELETE]: (user) => {
    // Same owner/admin gate as archive; unused clients only (server re-checks deps).
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.CLIENT_BLAST_RADIUS]: (user) => {
    // Owner/admin only — planning payload may include finance/payroll/workspace metadata.
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.READ);
  },
  [CAPABILITY.MATERIAL_CATALOG_SEARCH]: (user) => {
    if (isGuest(user)) return hidden();
    if (isAdmin(user)) return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
    if (isManager(user)) return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.READ);
    return grant(DATA_SCOPE.SELF, VISIBILITY.SELF);
  },
  [CAPABILITY.MATERIAL_CATALOG_VIEW_PRICING]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    if (isAdmin(user)) return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.READ);
  },
  [CAPABILITY.MATERIAL_CATALOG_EDIT]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.MATERIAL_USAGE_VIEW]: (user) => {
    if (isGuest(user)) return hidden();
    if (isAdmin(user)) return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
    if (isManager(user)) return grant(DATA_SCOPE.TEAM, VISIBILITY.EDIT);
    return grant(DATA_SCOPE.SELF, VISIBILITY.SELF);
  },
  [CAPABILITY.MATERIAL_USAGE_MANUAL]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    return grant(isAdmin(user) ? DATA_SCOPE.ORGANIZATION : DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  [CAPABILITY.MATERIAL_USAGE_VOID]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    return grant(isAdmin(user) ? DATA_SCOPE.ORGANIZATION : DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  // Task Catalog: managers browse (read-only), org owner/admin manage (as Materials).
  [CAPABILITY.TASK_CATALOG_VIEW]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, isAdmin(user) ? VISIBILITY.EDIT : VISIBILITY.READ);
  },
  [CAPABILITY.TASK_CATALOG_EDIT]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  // Project Tasks: everyone signed in sees and completes; managers create / edit / delete.
  [CAPABILITY.PROJECT_TASK_VIEW]: (user) => {
    if (isGuest(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.PROJECT_TASK_COMPLETE]: (user) => {
    if (isGuest(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.PROJECT_TASK_MANAGE]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    return grant(isAdmin(user) ? DATA_SCOPE.ORGANIZATION : DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  // Client View: owner/admin/manager share a project and publish pictures.
  [CAPABILITY.PROJECT_CLIENT_VIEW_MANAGE]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    return grant(isAdmin(user) ? DATA_SCOPE.ORGANIZATION : DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  // Planning: a read-only projection of dates. Same reach as Projects: every
  // signed-in role; employees only ever receive the projects they may already see.
  [CAPABILITY.SCREEN_PLANNING]: (user) => CAPABILITY_EVALUATORS[CAPABILITY.PROJECT_VIEW](user),
  [CAPABILITY.SCREEN_TASKS]: (user) =>
    CAPABILITY_EVALUATORS[CAPABILITY.TASK_CATALOG_VIEW](user),
  [CAPABILITY.ESTIMATE_VIEW]: (user) => managerPlusGrant(user),
  [CAPABILITY.ESTIMATE_EDIT]: (user) => managerPlusGrant(user),
  [CAPABILITY.ESTIMATE_APPROVE]: (user) => managerPlusGrant(user),
  [CAPABILITY.ESTIMATE_CONVERT]: (user) => managerPlusGrant(user),
  [CAPABILITY.INVOICE_VIEW]: (user) => managerPlusGrant(user),
  [CAPABILITY.INVOICE_REVIEW]: (user) => managerPlusGrant(user),
  [CAPABILITY.INVOICE_SEND]: (user) => managerPlusGrant(user),
  [CAPABILITY.INVOICE_MARK_PAID]: (user) => managerPlusGrant(user),
  [CAPABILITY.FINANCIAL_QUOTED_VIEW]: (user) => managerPlusGrant(user),
  [CAPABILITY.FINANCIAL_ACTUAL_VIEW]: (user) => managerPlusGrant(user),
  [CAPABILITY.FINANCIAL_BILLED_VIEW]: (user) => managerPlusGrant(user),
  [CAPABILITY.FINANCIAL_MARGIN_VIEW]: (user) => managerPlusGrant(user),
  [CAPABILITY.PAYROLL_QUEUE]: (user, _context, policies) =>
    evaluatePayrollCapability(user, policies, DATA_SCOPE.SELF, VISIBILITY.SELF),
  [CAPABILITY.PAYROLL_REPORT]: (user, _context, policies) =>
    evaluatePayrollCapability(user, policies, DATA_SCOPE.SELF, VISIBILITY.SELF),
  [CAPABILITY.PAYROLL_EXPORT]: (user, _context, policies) =>
    evaluatePayrollCapability(user, policies, DATA_SCOPE.SELF, VISIBILITY.SELF),
  [CAPABILITY.PAYROLL_MARK_PAID]: (user, _context, policies) =>
    evaluatePayrollCapability(user, policies, DATA_SCOPE.SELF, VISIBILITY.SELF),
  [CAPABILITY.PAYROLL_RATES_EDIT]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.TRADES_EDIT]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.USERS_VIEW]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    if (isAdmin(user)) return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
    return grant(DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  [CAPABILITY.USERS_EDIT]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.USERS_EDIT_REPORTS]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    if (isAdmin(user)) return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
    return grant(DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  [CAPABILITY.DASHBOARD_ACTIVE_CREW]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    return grant(isAdmin(user) ? DATA_SCOPE.ORGANIZATION : DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  [CAPABILITY.ACCOUNTS_PAYABLE_VIEW]: (user) => {
    if (!managerPayrollOrgAllowed(user)) return hidden();
    if (isAdmin(user)) {
      return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
    }
    return grant(DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  [CAPABILITY.ACCOUNTS_RECEIVABLE_VIEW]: (user) =>
    CAPABILITY_EVALUATORS[CAPABILITY.INVOICE_VIEW](user),
  [CAPABILITY.MISSION_CONTROL_OPERATIONAL]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.READ);
  },
  [CAPABILITY.MISSION_CONTROL_DEVELOPMENT]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.READ);
  },
  [CAPABILITY.REPORT_BUILDER_VIEW]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    if (isAdmin(user)) return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
    return grant(DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  [CAPABILITY.PROJECT_ANALYTICS_VIEW]: (user) => managerPlusGrant(user),
  [CAPABILITY.SCREEN_MODE_SELECT]: (user) => {
    if (isGuest(user)) return hidden();
    return grant(DATA_SCOPE.SELF, VISIBILITY.EDIT);
  },
  [CAPABILITY.SCREEN_KPI_DASHBOARD]: (user) => {
    if (isGuest(user)) return hidden();
    if (isAdmin(user)) return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
    if (isManager(user)) return grant(DATA_SCOPE.TEAM, VISIBILITY.EDIT);
    return grant(DATA_SCOPE.SELF, VISIBILITY.SELF);
  },
  [CAPABILITY.SCREEN_TIME_TRACKING]: (user) => {
    if (isGuest(user)) return hidden();
    return grant(DATA_SCOPE.SELF, VISIBILITY.EDIT);
  },
  [CAPABILITY.SCREEN_COCKPIT]: (user) => {
    if (isGuest(user)) return hidden();
    if (isEmployee(user)) return grant(DATA_SCOPE.SELF, VISIBILITY.SELF);
    return grant(DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  [CAPABILITY.SCREEN_MANUAL_TIME_ENTRY]: (user) => {
    if (isGuest(user)) return hidden();
    if (isAdmin(user)) return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
    if (isManager(user)) return grant(DATA_SCOPE.TEAM, VISIBILITY.EDIT);
    return grant(DATA_SCOPE.SELF, VISIBILITY.SELF);
  },
  [CAPABILITY.SCREEN_CREATE_CLIENT]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    return grant(DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  [CAPABILITY.SCREEN_CREATE_PROJECT]: (user) => {
    if (isGuest(user)) return hidden();
    return CAPABILITY_EVALUATORS[CAPABILITY.PROJECT_VIEW](user);
  },
  [CAPABILITY.SCREEN_PROJECT_DETAIL]: (user, context) =>
    CAPABILITY_EVALUATORS[CAPABILITY.PROJECT_DETAIL_OPERATIONAL](user, context),
  [CAPABILITY.SCREEN_INTELLIGENCE]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    return grant(isAdmin(user) ? DATA_SCOPE.ORGANIZATION : DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  [CAPABILITY.SCREEN_PAYROLL_HOME]: (user, context, policies) =>
    evaluatePayrollCapability(user, policies, DATA_SCOPE.SELF, VISIBILITY.SELF),
  [CAPABILITY.SCREEN_USER_MANAGEMENT]: (user) => {
    if (!isManagerOrAdmin(user)) return hidden();
    return grant(isAdmin(user) ? DATA_SCOPE.ORGANIZATION : DATA_SCOPE.TEAM, VISIBILITY.EDIT);
  },
  [CAPABILITY.SCREEN_USER_PREFERENCES]: (user) => {
    if (isGuest(user) || !user?.supabase_user_id) return hidden();
    return grant(DATA_SCOPE.SELF, VISIBILITY.EDIT);
  },
  [CAPABILITY.SCREEN_SETTINGS]: (user) =>
    CAPABILITY_EVALUATORS[CAPABILITY.SCREEN_USER_PREFERENCES](user),
  [CAPABILITY.SCREEN_ADMINISTRATION]: (user) =>
    CAPABILITY_EVALUATORS[CAPABILITY.SCREEN_USER_MANAGEMENT](user),
  [CAPABILITY.ADMIN_SETTINGS_VIEW]: (user) =>
    CAPABILITY_EVALUATORS[CAPABILITY.SCREEN_USER_MANAGEMENT](user),
  [CAPABILITY.ADMIN_ORGANIZATION_MANAGE]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_BILLING_VIEW]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.VIEW);
  },
  [CAPABILITY.ADMIN_USERS_MANAGE]: (user) =>
    CAPABILITY_EVALUATORS[CAPABILITY.USERS_EDIT](user),
  [CAPABILITY.ADMIN_ROLES_MANAGE]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_TRADES_MANAGE]: (user) =>
    CAPABILITY_EVALUATORS[CAPABILITY.TRADES_EDIT](user),
  [CAPABILITY.ADMIN_CLASSIFICATIONS_MANAGE]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_WORK_TYPES_MANAGE]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_PAYROLL_TYPES_MANAGE]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_PAYROLL_SETTINGS]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_PROJECTS_SETTINGS]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_MATERIALS_SETTINGS]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_ESTIMATES_SETTINGS]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_INVOICES_SETTINGS]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_REPORTS_SETTINGS]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_NOTIFICATIONS_SETTINGS]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_OFFLINE_SETTINGS]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_HELP_LEARNING_SETTINGS]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_BRANDING_MANAGE]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_INTEGRATIONS_MANAGE]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_FEATURES_MANAGE]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.ADMIN_ADVANCED_MANAGE]: (user) => {
    if (!isAdmin(user)) return hidden();
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.SCREEN_ESTIMATE_HUB]: (user) => {
    const result = managerPlusGrant(user);
    return result.allowed ? result : hidden();
  },
  [CAPABILITY.SCREEN_INVOICE_HUB]: (user) => {
    const result = managerPlusGrant(user);
    return result.allowed ? result : hidden();
  },
  [CAPABILITY.SCREEN_MATERIALS]: (user) =>
    CAPABILITY_EVALUATORS[CAPABILITY.MATERIAL_CATALOG_SEARCH](user),
  [CAPABILITY.SCREEN_ACCOUNTS_PAYABLE]: (user) =>
    CAPABILITY_EVALUATORS[CAPABILITY.ACCOUNTS_PAYABLE_VIEW](user),
  [CAPABILITY.SCREEN_ACCOUNTS_RECEIVABLE]: (user) =>
    CAPABILITY_EVALUATORS[CAPABILITY.ACCOUNTS_RECEIVABLE_VIEW](user),
  [CAPABILITY.SCREEN_MISSION_CONTROL]: (user) =>
    CAPABILITY_EVALUATORS[CAPABILITY.MISSION_CONTROL_OPERATIONAL](user),
  [CAPABILITY.FINANCE_MODULE]: (user, _context, policies) => {
    if (!policies?.financeModuleEnabled) {
      return hidden();
    }
    // Module permission — not role-based and not Client Workspace membership.
    if (user?.finance_access !== true) {
      return hidden();
    }
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.FINANCE_MANAGE]: (user, _context, policies) => {
    if (!policies?.financeModuleEnabled || user?.finance_access !== true) {
      return hidden();
    }
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  },
  [CAPABILITY.FINANCE_RECONCILE]: (user, _context, policies) => {
    if (!policies?.financeModuleEnabled || user?.finance_access !== true) {
      return hidden();
    }
    if (isAdmin(user)) {
      return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
    }
    return hidden();
  },
  [CAPABILITY.SCREEN_FINANCE]: (user, context, policies) =>
    CAPABILITY_EVALUATORS[CAPABILITY.FINANCE_MODULE](user, context, policies),
};

function managerPlusGrant(user) {
  if (!isManagerOrAdmin(user)) {
    return hidden();
  }

  if (isAdmin(user)) {
    return grant(DATA_SCOPE.ORGANIZATION, VISIBILITY.EDIT);
  }

  return grant(DATA_SCOPE.TEAM, VISIBILITY.EDIT);
}

function evaluateCapability(user, capability, context = {}, orgPolicies) {
  const normalized = normalizeActor(user);
  const policies = orgPolicies || resolveOrgPolicies(context.orgSettings);

  if (!isMissionControlEnabled() && isMissionControlCapability(capability)) {
    return hidden();
  }

  if (isGuestActor(normalized) && isGuestTourCapability(capability)) {
    return guestDemoGrant();
  }

  const evaluator = CAPABILITY_EVALUATORS[capability];

  if (!evaluator) {
    return hidden();
  }

  return evaluator(normalized, context, policies);
}

/**
 * Full permission evaluation result.
 */
export function getPermission(user, capability, context = {}, orgPolicies) {
  return evaluateCapability(user, capability, context, orgPolicies);
}

export function can(user, capability, context = {}, orgPolicies) {
  return getPermission(user, capability, context, orgPolicies).allowed;
}

export function getVisibilityLevel(user, capability, context = {}, orgPolicies) {
  return getPermission(user, capability, context, orgPolicies).visibility;
}

export function getEffectiveScope(user, capability, context = {}, orgPolicies) {
  const { allowed, scope } = getPermission(
    user,
    capability,
    context,
    orgPolicies,
  );

  return allowed ? scope : null;
}

export function assertCan(
  user,
  capability,
  context = {},
  orgPolicies,
  message,
) {
  if (!can(user, capability, context, orgPolicies)) {
    throw new PermissionError(
      capability,
      message || `Missing capability: ${capability}`,
    );
  }
}

export function listCapabilities() {
  return Object.values(CAPABILITY);
}

export function isCapabilityRegistered(capability) {
  return Object.prototype.hasOwnProperty.call(CAPABILITY_EVALUATORS, capability);
}
